import React from 'react';
import { Form, Input } from 'antd';

import { Identifiable, RecordModel } from '../core';

/**
 * Tenancy — the platform's operators (tenants) and their presentation config.
 *
 * Mirrors `tenancy.Operator` / `tenancy.OperatorBranding` in ancana_api. The
 * `theme_tokens` shape is owned by the FRONTEND (see the model docstring): Caim
 * defines it in `lib/theme.ts` and `lib/vocabulary.ts`, the backend stores and
 * serves the JSON, and this file is the office's copy of the same contract.
 * Anything added to a preset here has to exist in Caim first, or a tenant will
 * be configured with a key the portal cannot render.
 */

/** What an operator's portfolio *is* — Caim's `lib/vocabulary.ts:ASSET_TYPES`. */
export const ASSET_TYPES = ['home', 'yacht', 'jet', 'rv'] as const;
export type AssetType = typeof ASSET_TYPES[number];
export const DEFAULT_ASSET_TYPE: AssetType = 'home';

/**
 * Just enough of each preset to label it and show a worked example.
 *
 * Deliberately NOT a copy of Caim's full grammar engine (ten derived forms per
 * noun, per locale). The office is a configuration screen, not a rendering of
 * the portal, and a second copy of that engine would drift from the one that
 * actually ships the copy — showing an operator wording their owners will never
 * see. What matters here is that the picker names the right thing and that the
 * asset-specific identifier label is visible, since it is the field an operator
 * most often expects to be wrong.
 */
export const ASSET_PRESETS: Record<AssetType, {
  label: string;
  /** The article-carrying form, which is where a noun swap usually reads wrong. */
  sample: string;
  /** What `Unit.number` is called for this class of asset. */
  numberLabel: string;
  icon: string;
}> = {
  home: { label: 'Homes', sample: 'a home', numberLabel: 'Unit no.', icon: '🏡' },
  yacht: { label: 'Yachts', sample: 'a yacht', numberLabel: 'Hull no.', icon: '⛵️' },
  jet: { label: 'Jets', sample: 'a jet', numberLabel: 'Tail no.', icon: '✈️' },
  rv: { label: 'RVs', sample: 'an RV', numberLabel: 'VIN', icon: '🚐' },
};

/** How a tenant's portal responds to the viewer's OS preference. */
export const THEME_MODES = ['system', 'light', 'dark'] as const;
export type ThemeMode = typeof THEME_MODES[number];
export const DEFAULT_THEME_MODE: ThemeMode = 'system';

export const THEME_MODE_LABELS: Record<ThemeMode, { label: string; hint: string }> = {
  system: { label: 'Match device', hint: "Follows each owner's own light/dark setting." },
  light: { label: 'Always light', hint: 'Every owner sees the light theme.' },
  dark: { label: 'Always dark', hint: 'Every owner sees the dark theme.' },
};

/** Caim's `lib/theme.ts:PLATFORM_DEFAULTS` — what a tenant gets when unset. */
export const PLATFORM_DEFAULTS = {
  primary: '#0f766e',
  surface: '#f4f4f5',
  radius: '0.625rem',
};

export interface IVocabularyConfig {
  assetType?: AssetType;
  /** Overrides the preset's noun — "villa" rather than "home". */
  one?: string;
  many?: string;
  gender?: 'm' | 'f';
  /** English: takes "an" rather than "a". Stored, not derived from the spelling. */
  an?: boolean;
}

/**
 * The stored token blob.
 *
 * Only `primary` and `surface` are read by Caim's renderer — the rest of the
 * neutral ramp (background, text, borders, the text color on the primary fill)
 * is DERIVED from the surface hue so the portal stays WCAG AA by construction.
 * The legacy keys stay legal in stored JSON but are inert, so this office panel
 * does not offer them: an editable field whose value is ignored is worse than no
 * field at all.
 */
export interface IBrandTokens {
  colors?: {
    primary?: string;
    /** Read as a HUE, not a literal color — seeds the whole neutral ramp. */
    surface?: string;
    [legacyKey: string]: string | undefined;
  };
  mode?: ThemeMode;
  radius?: string;
  fontSans?: string;
  fontMono?: string;
  vocabulary?: IVocabularyConfig;
}

export interface IOperatorBranding {
  theme_tokens: IBrandTokens;
  logo_url: string;
  logo_dark_url: string;
  favicon_url: string;
}

export type OperatorStatus = 'active' | 'suspended';

/**
 * The optional product modules a tenant can have switched on. Mirrors
 * `ancana_api/tenancy/modules.py` OPTIONAL (keys and labels, in its order);
 * the API rejects unknown keys. Core modules (properties, owners, calendar,
 * finances) are always on and never listed.
 */
export const OPTIONAL_MODULES = [
  { key: 'booking_rounds', label: 'Booking rounds' },
  { key: 'exchanges', label: 'Exchanges' },
  { key: 'inspections', label: 'Inspections' },
  { key: 'owner_billing', label: 'Owner billing' },
] as const;
export type OptionalModule = (typeof OPTIONAL_MODULES)[number]['key'];

/**
 * A bundle of module defaults a tenant starts from (`tenancy.Plan`). Editing a
 * plan changes what NEW tenants start with, never existing ones; a trial is a
 * state of a plan (`IOperator.trial_ends_at`), not a plan.
 */
export interface IPlan {
  id: number;
  key: string;
  name: string;
  modules: OptionalModule[];
  trial_days: number;
  is_trial_default: boolean;
  position: number;
  operator_count: number;
}

export interface IOperator extends Identifiable {
  id: number;
  name: string;
  slug: string;
  status: OperatorStatus;
  /** Read-only: which operator *is* the platform is a deployment fact. */
  is_platform_owner: boolean;
  brand_name: string;
  sender_email: string;
  support_email: string;
  portal_base_url: string;
  admin_base_url: string;
  slack_channel: string;
  default_locale: string;
  country: string;
  timezone: string;
  /** Optional modules switched on; a new tenant starts with all of them. */
  modules: OptionalModule[];
  /** Set by the tenant's admin in caim Settings → Business. */
  exchange_partner: string;
  plan: number | null;
  plan_name: string;
  /** Last day of the trial (YYYY-MM-DD); past it the tenant is suspended. Null: not on a trial. */
  trial_ends_at: string | null;
  /** Read-only: days left counting today, 0 once over, null when not on a trial. */
  trial_days_left: number | null;
  /** Null only for tenants created before the API started seeding a branding row. */
  branding: IOperatorBranding | null;
  member_count: number;
  created_at: string;
  modified_at: string;
}

export const EMPTY_BRANDING: IOperatorBranding = {
  theme_tokens: {},
  logo_url: '',
  logo_dark_url: '',
  favicon_url: '',
};

export const assetTypeOf = (operator: IOperator): AssetType => {
  const stored = operator.branding?.theme_tokens?.vocabulary?.assetType;
  return stored && ASSET_TYPES.includes(stored) ? stored : DEFAULT_ASSET_TYPE;
};

/** A stored color, or the platform default it falls back to at render time. */
export const colorOf = (
  operator: IOperator, key: 'primary' | 'surface',
): string => operator.branding?.theme_tokens?.colors?.[key] || PLATFORM_DEFAULTS[key];

/**
 * Minimal record model for an operator, so the generic machinery can resolve
 * `operator` foreign keys (`RelatedFieldSelect` / `RelatedFieldCell` look up
 * `<FieldName>Model` by name). The Tenants panel does its own fetching and does
 * not go through `RecordTable` — see `src/pages/tenancy/Tenants.tsx` for why.
 */
export const OperatorModel: RecordModel<IOperator> = {
  name: 'Tenant',
  namePlural: 'Tenants',
  path: 'tenancy/operators',
  selectQuery: '',
  tableQuery: '',
  toString: r => r.brand_name || r.name,
};

export interface IMembership extends Identifiable {
  id: number;
  user: number;
  user_email: string;
  user_name: string;
  operator: number;
  operator_name: string;
  role: MembershipRole;
  is_active: boolean;
  /** Read-only: they haven't set a password yet, so their invite is still open. */
  invite_pending: boolean;
  created_at: string;
  modified_at: string;
}

export type MembershipRole =
  | 'operator_admin'
  | 'operator_ops'
  | 'operator_finance'
  | 'operator_field'
  | 'platform_staff'
  | 'compliance';

/**
 * Staff of one tenant.
 *
 * Owners are deliberately absent: owner tenancy is derived through
 * `UnitOwner -> Unit -> Operator` and grants no membership row. So this table is
 * the staff roster, and "add" here means granting an EXISTING user a role. New
 * people come in through invites (the New tenant drawer's first admin, or the
 * tenant's own Team page in caim); the Tenants page adds an Invite column.
 *
 * Always rendered scoped to one tenant (`recordsetFilters={{ operator }}`),
 * which both filters the list and pre-fills the create form. `operator` is
 * therefore kept in the form as a hidden field rather than a picker: a visible
 * one could be changed, silently filing the grant under a different tenant than
 * the table the person is looking at.
 */
export const MembershipModel: RecordModel<IMembership> = {
  name: 'Staff member',
  namePlural: 'Staff',
  path: 'tenancy/memberships',
  selectQuery: '',
  tableQuery: '',
  toString: r => `${r.user_name || r.user_email} (${r.operator_name})`,
  primaryKeyName: 'id',
  allowSearch: true,
  fieldModels: {
    user: 'User',
    operator: 'Operator',
  },
  customTableMappings: {
    user: { title: 'User', width: 260 },
    role: { title: 'Role', width: 180 },
    is_active: { title: 'Active', width: 80, align: 'center' },
    user_email: { omit: true },
    user_name: { omit: true },
    operator_name: { omit: true },
    modified_at: { omit: true },
    created_at: { title: 'Granted', width: 140 },
  },
  customCreateFields: [
    {
      name: 'operator',
      component: (
        <Form.Item name="operator" hidden>
          <Input />
        </Form.Item>
      ),
    },
  ],
};
